// ─── Recibo en texto plano (WhatsApp / compartir) ────────────────────────────
//
// Única fuente de verdad del texto del recibo que se manda por wa.me desde el
// ticket del POS (CarritoPOS) y desde el historial (/ventas). Antes vivía
// dentro de CarritoPOS y el historial no tenía cómo reusarlo.
//
// Reglas (pedido del cliente piloto): SIN emojis ni símbolos raros (×, —, ▫,
// ✅) — en varios celulares llegaban como "?". Solo ASCII + tildes.
//
// `orden` es el shape de `ordenConfirmada` de CarritoPOS (también lo arma
// Ventas.jsx al reimprimir): lineas [{name|nombre, quantity|cantidad,
// subtotal, unitPrice|precio, nota}], subtotal, propina {porcentaje, monto},
// canje {nombre, monto}, total, metodo, fecha (Date o string ISO), id,
// numFactura, mesaNumero, cajero.

import { formatCOP } from './formatters';

// Etiquetas de método de pago: acepta los ids del POS ('efectivo'…) y los
// enums del backend ('CASH'…), porque el historial trae los segundos.
export const METODO_PAGO_LABEL = {
  efectivo:      'Efectivo',
  datafono:      'Datáfono',
  nequi:         'Nequi',
  daviplata:     'Daviplata',
  transferencia: 'Transferencia',
  CASH:          'Efectivo',
  CARD:          'Datáfono',
  BOLD:          'Datáfono',
  NEQUI:         'Nequi',
  DAVIPLATA:     'Daviplata',
  TRANSFER:      'Transferencia',
};

export function labelMetodoPago(metodo) {
  if (!metodo) return null;
  return METODO_PAGO_LABEL[metodo] ?? String(metodo);
}

// Nombre de quien atendió, APTO PARA UN DESCONOCIDO.
//
// El POS arma `cajero` como `empleadoActivo?.nombre ?? user?.email`, así que
// cuando el dueño cobra sin seleccionar empleado ahí cae SU CORREO PERSONAL —
// y ese recibo se lo mandamos al cliente. Eso no es un detalle estético: es
// repartir la dirección personal del dueño entre desconocidos, y además queda
// impresa en el ticket de papel.
//
// Regla: si parece un correo, NO se muestra. Se prefiere omitir la línea antes
// que inventar un nombre a partir del correo ("juaneslizcanom" tampoco es algo
// que uno quiera darle a un cliente). El dueño pone su nombre en
// Configuración → Tu cuenta y desde ahí sale bonito.
export function nombreQueAtendio(valor) {
  const s = String(valor ?? '').trim();
  if (!s || s.includes('@')) return null;
  return s;
}

// Número corto legible de la orden: numFactura si existe, si no los últimos 6
// del id. Tolera id numérico (mock) y ausencia de id (venta offline).
export function numeroOrdenCorto(orden) {
  if (orden?.numFactura != null && orden.numFactura !== '') return String(orden.numFactura);
  const id = orden?.id;
  if (id == null || id === '') return null;
  return String(id).slice(-6).toUpperCase();
}

// Fecha tolerante: Date, string ISO o nada (→ ahora). Una fecha inválida
// también cae a "ahora" en vez de imprimir "Invalid Date".
export function fechaOrden(orden) {
  const f = orden?.fecha ?? orden?.createdAt;
  const d = f instanceof Date ? f : (f ? new Date(f) : new Date());
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

// Texto de una línea: "- 2x Café (Grande)   $9.000" + nota indentada.
function lineaTexto(l) {
  const cant   = l.quantity ?? l.cantidad ?? 1;
  const nombre = l.name ?? l.nombre ?? 'Producto';
  const sub    = l.subtotal ?? (Number(l.unitPrice ?? l.precio ?? 0) * Number(cant));
  const base   = `- ${cant}x ${nombre}   ${formatCOP(sub)}`;
  return l.nota ? `${base}\n    (${l.nota})` : base;
}

export function textoReciboWhatsApp(orden, negocio) {
  if (!orden) return '';
  const lineas  = orden.lineas ?? orden.items ?? [];
  const fecha   = fechaOrden(orden);
  const fechaStr = fecha.toLocaleString('es-CO', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const numero  = numeroOrdenCorto(orden);
  const nombreNegocio = negocio?.razonSocial ?? negocio?.name ?? 'mezo';

  const cabecera = [
    `*Recibo - ${nombreNegocio}*`,
    `Orden #${numero ?? 'OFFLINE'} | ${fechaStr}`,
    orden.mesaNumero != null ? `Mesa ${orden.mesaNumero}` : '',
  ];

  const totales = [
    orden.subtotal != null ? `Subtotal: ${formatCOP(orden.subtotal)}` : '',
    orden.canje?.monto > 0
      ? `Canje${orden.canje.nombre ? ` ${orden.canje.nombre}` : ''}: -${formatCOP(orden.canje.monto)}`
      : '',
    orden.propina?.monto > 0
      ? `Propina${orden.propina.porcentaje ? ` (${orden.propina.porcentaje}%)` : ''}: ${formatCOP(orden.propina.monto)}`
      : '',
    `*Total: ${formatCOP(orden.total)}*`,
  ];

  const metodoLabel = orden.metodoNombre ?? labelMetodoPago(orden.metodo);
  const pago = [
    metodoLabel ? `Pago: ${metodoLabel}` : '',
    orden.recibido != null ? `Recibido: ${formatCOP(orden.recibido)}` : '',
    orden.recibido != null ? `Cambio: ${formatCOP(orden.cambio ?? 0)}` : '',
  ];

  return [
    ...cabecera,
    '',
    ...lineas.map(lineaTexto),
    '',
    ...totales,
    '',
    ...pago,
    nombreQueAtendio(orden.cajero) ? `Te atendio: ${nombreQueAtendio(orden.cajero)}` : '',
    'Gracias por tu visita!',
  ].filter((s) => s !== '').join('\n');
}

// URL wa.me lista: con número destino (57…) o sin destino (el cajero elige).
export function urlWhatsAppRecibo(texto, celularNormalizado = '') {
  const dest = celularNormalizado ? `/${celularNormalizado}` : '/';
  return `https://wa.me${dest}?text=${encodeURIComponent(texto)}`;
}
